import type { FeedbackCategory, FeedbackTriageState, SubmitFeedbackRequest } from '@/api/types'

/**
 * Pure helpers behind the "Send feedback" dialog and the staff inbox. No React, so the rules that
 * matter -- the byte cap, what may be submitted, what the redaction notice says -- are testable
 * without a DOM.
 */

/** The server's cap on a report body, in UTF-8 bytes. */
export const FEEDBACK_MAX_BODY_BYTES = 64 * 1024
/** The server's cap on a title, in characters. */
export const FEEDBACK_MAX_TITLE_CHARS = 200

export const FEEDBACK_CATEGORIES: readonly { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'cost', label: 'Cost' },
  { value: 'protocol', label: 'Protocol' },
  { value: 'installer', label: 'Installer' },
  { value: 'ux', label: 'Console / UX' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Other' },
]

export const TRIAGE_STATES: readonly FeedbackTriageState[] = [
  'new',
  'triaged',
  'actioned',
  'dismissed',
]

/** UTF-8 size, which is what the server counts -- not `string.length`. */
export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length
}

export interface FeedbackDraft {
  category: FeedbackCategory
  title: string
  body: string
  /** Empty string means "no zone". */
  zoneId: string
}

/** The first reason this draft cannot be sent, or `undefined` when it can. */
export function feedbackProblem(draft: FeedbackDraft): string | undefined {
  if (draft.body.trim().length === 0) return 'Write something in the message first.'
  if (utf8Bytes(draft.body) > FEEDBACK_MAX_BODY_BYTES) {
    return 'The message is over 64 KiB. Shorten it.'
  }
  if ([...draft.title.trim()].length > FEEDBACK_MAX_TITLE_CHARS) {
    return `The title is over ${String(FEEDBACK_MAX_TITLE_CHARS)} characters.`
  }
  return undefined
}

/** The request body: blank optional fields are left out rather than sent empty. */
export function toRequest(draft: FeedbackDraft): SubmitFeedbackRequest {
  const title = draft.title.trim()
  return {
    category: draft.category,
    body: draft.body,
    ...(title.length > 0 ? { title } : {}),
    ...(draft.zoneId.length > 0 ? { zone_id: draft.zoneId } : {}),
  }
}

/** What the success screen says about redaction, or `undefined` when nothing was scrubbed. */
export function redactionNotice(redactions: number): string | undefined {
  if (redactions <= 0) return undefined
  return redactions === 1
    ? '1 value that looked like a secret was removed from your message before it was stored.'
    : `${String(redactions)} values that looked like secrets were removed from your message before it was stored.`
}

/** A `YYYY-MM-DD` day, as the local day's start (inclusive) -> ISO instant. */
export function dayStartIso(day: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (match === null) return undefined
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** The end of a local day, as the API's half-open `until`: the start of the NEXT day. */
export function dayEndIso(day: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (match === null) return undefined
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

export interface InboxFilters {
  category: string
  state: string
  zone: string
  client: string
  q: string
  /** `YYYY-MM-DD` or empty. */
  from: string
  to: string
}

export const NO_FILTERS: InboxFilters = {
  category: '',
  state: '',
  zone: '',
  client: '',
  q: '',
  from: '',
  to: '',
}

/** The API's query parameters for these filters; blank ones are left out. */
export function toApiFilters(filters: InboxFilters): Record<string, string> {
  const out: Record<string, string> = {}
  if (filters.category !== '') out['category'] = filters.category
  if (filters.state !== '') out['state'] = filters.state
  if (filters.zone.trim() !== '') out['zone'] = filters.zone.trim()
  if (filters.client.trim() !== '') out['client'] = filters.client.trim()
  if (filters.q.trim() !== '') out['q'] = filters.q.trim()
  const since = dayStartIso(filters.from)
  if (since !== undefined) out['since'] = since
  const until = dayEndIso(filters.to)
  if (until !== undefined) out['until'] = until
  return out
}
